/**
 * Filtering for blog listings (the Inspiration pages).
 * ---------------------------------------------------------------------------
 * Shopify has no faceted filtering for blogs, only for collections, so this
 * does the work in the browser. Every post is already on the page, which makes
 * filtering instant and freely combinable: no reload, no extra requests.
 *
 * Each post card carries its values as data attributes (see
 * snippets/blog-filter-values.liquid):
 *
 *     <li data-f-designers="John Pawson|Cecilie Manz" data-f-tones="White">
 *
 * The options in each dropdown are built from those cards, so a filter can only
 * ever offer a value that some post actually has. Within one filter the values
 * are OR'ed ("Green or Blue"), across filters they are AND'ed ("Green AND
 * Bathrooms") — the same behaviour as the product filters.
 *
 * A filter does not have to appear as a dropdown. The bar can also show single
 * values as words of their own — Journal does this, so the reader clicks
 * "Events" rather than opening a "Type" first. Several of those can stand for
 * the same underlying filter, which is why the selection is kept per filter
 * (a bucket) rather than per button: Events and Essays are two buttons but one
 * filter, so having both on shows both kinds of post instead of none.
 *
 * Values are compared case-insensitively and trimmed, so "Fashion designers"
 * and "Fashion Designers" collapse into a single option. The spelling that is
 * shown is the first one encountered.
 *
 * Active filters are mirrored in the query string (?f_tones=Green), so a
 * filtered view can be linked to and survives a reload or a back navigation.
 */

const PARAM_PREFIX = 'f_';

/** Filters that always get a search field, however few values are tagged yet. */
const ALWAYS_SEARCHABLE = ['colour'];

/** Above this many values a filter gets a search field of its own. */
const SEARCH_THRESHOLD = 8;

/** How long the listing stays faded while it is being refiltered. */
const FADE_MS = 140;

/**
 * Dropdowns read down, not across: a list fills one column before a second one
 * starts, so seven filters look alike whether they hold two values or twenty.
 * From this many values a second column opens. Eight is where the sketch put
 * the line, and eight is also what Colour holds today: at exactly eight the
 * list splits four and four, the way the sketch drew it, rather than standing
 * as one long column of eight.
 */
const OPTIONS_PER_COLUMN = 8;

/**
 * And never more than this. The colours run to several hundred, and a wall of
 * columns is the very thing the single column is there to avoid — past this
 * point the list scrolls, and the search field is the way through it.
 */
const MAX_OPTION_COLUMNS = 2;

/**
 * Colours are not listed alphabetically. They follow the order they are thought
 * of in: light to dark, then the warm end. Everything else — designers,
 * locations, collections — stays alphabetical.
 */
const COLOUR_ORDER = [
  'white',
  'grey',
  'black',
  'beige',
  'brown',
  'green',
  'blue',
  'purple',
  'red',
  'pink',
  'orange',
  'yellow',
];

/**
 * Spellings that mean one of the colours above.
 *
 * Red and pink are two tones, not one. Someone looking for a pink room is not
 * looking for a red one, and the products already say "Red" on its own — the
 * combined "Red/Pink" only ever existed in the text field on the posts, where
 * it sat next to the products' "Red" as a second option for the same thing.
 * A post left with the old spelling still sorts beside red rather than falling
 * to the bottom of the list.
 */
const COLOUR_ALIASES = {
  gray: 'grey',
  'red/pink': 'red',
  'pink/red': 'red',
};

/** The filters whose values follow COLOUR_ORDER. Paint calls its own "Tone". */
const COLOUR_ORDERED_KEYS = ['colour', 'tones'];

/** Lowercased, whitespace-collapsed key used for comparing two values. */
function normalise(value) {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Where a colour sits in COLOUR_ORDER, or -1 if it is not one we know. Spaces
 * are dropped first, so "Red / Pink" and "Red/Pink" are the same colour.
 */
function colourRank(label) {
  const key = label.replace(/\s+/g, '').toLowerCase();
  const canonical = COLOUR_ALIASES[key] || key;
  return COLOUR_ORDER.indexOf(canonical);
}

/** The values one card has for one filter, already split and cleaned. */
function cardValues(card, key) {
  const raw = card.getAttribute(`data-f-${key}`);
  if (!raw) return [];
  return raw
    .split('|')
    .map((value) => value.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
}

class BlogFilter {
  constructor(root) {
    this.root = root;
    this.bar = root.querySelector('[data-blog-filter-bar]');
    this.results = root.querySelector('[data-blog-filter-results]');
    if (!this.bar || !this.results) return;

    this.cards = Array.from(this.results.querySelectorAll('[data-blog-filter-item]'));
    this.placeholder = this.results.querySelector('[data-blog-filter-placeholder]');
    this.emptyState = root.querySelector('[data-blog-filter-empty]');
    this.countEl = this.bar.querySelector('[data-blog-filter-result-count]');
    this.clearButton = this.bar.querySelector('[data-blog-filter-clear]');
    this.strings = {
      noMatches: this.bar.getAttribute('data-i18n-no-matches') || 'No matches for “[terms]”',
      resultCount: this.bar.getAttribute('data-i18n-result-count') || '[count] of [total]',
    };
    this.perRow = parseInt(this.results.getAttribute('data-per-row'), 10) || 3;
    this.itemColumnWidth = parseInt(this.results.getAttribute('data-item-col-width'), 10) || 4;

    // One bucket per filter, holding what is chosen in it. Buttons point at a
    // bucket rather than owning a set of their own, so the three Type buttons
    // in Journal add up to one Type choice.
    this.buckets = new Map();

    this.groups = Array.from(this.bar.querySelectorAll('[data-blog-filter-group]')).map((el, i) => {
      const key = el.getAttribute('data-blog-filter-group');
      if (!this.buckets.has(key)) this.buckets.set(key, { key, selected: new Set() });

      const chipAttr = el.getAttribute('data-blog-filter-chip');
      return {
        el,
        key,
        // Part of the checkbox ids: two dropdowns could share a filter, and two
        // inputs with the same id would tie their labels to the wrong one.
        index: i,
        bucket: this.buckets.get(key),
        // A word of its own: the values it stands for, already comparable.
        chip: chipAttr ? chipAttr.split('|').map(normalise).filter(Boolean) : null,
        panel: el.querySelector('[data-blog-filter-panel]'),
        toggle: el.querySelector('[data-blog-filter-toggle]'),
        countEl: el.querySelector('[data-blog-filter-count]'),
        optionsEl: el.querySelector('[data-blog-filter-options]'),
        searchWrap: el.querySelector('[data-blog-filter-search-wrap]'),
        searchEl: el.querySelector('[data-blog-filter-search]'),
        noOptionsEl: el.querySelector('[data-blog-filter-no-options]'),
      };
    });

    this.buildOptions();
    this.readUrl();
    this.bindEvents();
    this.apply();
  }

  /** Collect the distinct values each filter has across all cards. */
  buildOptions() {
    this.buckets.forEach((bucket) => {
      const seen = new Map();
      this.cards.forEach((card) => {
        cardValues(card, bucket.key).forEach((value) => {
          const id = normalise(value);
          if (!seen.has(id)) seen.set(id, value);
        });
      });

      const byColour = COLOUR_ORDERED_KEYS.includes(bucket.key);
      bucket.options = Array.from(seen.entries())
        .map(([id, label]) => ({ id, label }))
        .sort((a, b) => {
          if (byColour) {
            const rankA = colourRank(a.label);
            const rankB = colourRank(b.label);
            // A colour we know comes before one we do not, so a new spelling
            // lands at the bottom of the list instead of in the middle of it.
            if (rankA !== rankB && (rankA !== -1 || rankB !== -1)) {
              if (rankA === -1) return 1;
              if (rankB === -1) return -1;
              return rankA - rankB;
            }
          }
          return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
        });
    });

    this.groups.forEach((group) => {
      group.options = group.bucket.options;

      if (group.chip) {
        // Only the values some post actually carries. A category that has not
        // been tagged yet is not offered — and appears by itself the moment the
        // first post gets it, exactly like a value inside a dropdown.
        group.chip = group.chip.filter((value) =>
          group.options.some((option) => option.id === value)
        );
        group.el.hidden = group.chip.length === 0;
        return;
      }

      if (group.options.length === 0) {
        // Nothing tagged yet — hide the whole dropdown rather than show an
        // empty one.
        group.el.hidden = true;
        return;
      }

      // A search field only earns its place once the list is long — except for
      // the colours, which are around 400 and growing, so that one always has
      // it rather than appearing to change behaviour as posts are tagged.
      // display again, not the hidden attribute: the wrapper is a "block".
      group.searchIsShown =
        group.options.length > SEARCH_THRESHOLD || ALWAYS_SEARCHABLE.includes(group.key);
      if (group.searchWrap) {
        group.searchWrap.style.display = group.searchIsShown ? '' : 'none';
      }

      group.optionsEl.innerHTML = '';
      group.options.forEach((option, index) => {
        // Same markup as the product filters: a visually hidden checkbox with
        // the label's dot filled in when it is checked. The look lives in
        // snippets/blog-filter-bar.liquid — Tailwind is purged here, so the
        // states are written as plain CSS on these class names.
        const li = document.createElement('li');
        li.className = 'blog-filter-option';
        li.setAttribute('data-blog-filter-option', option.id);

        const id = `blog-filter-${group.key}-${group.index}-${index}`;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'filter-checkbox custom-input sr-only';
        input.value = option.id;
        input.id = id;

        const label = document.createElement('label');
        label.htmlFor = id;

        const dot = document.createElement('span');
        dot.className = 'blog-filter-dot';

        label.append(dot, document.createTextNode(option.label));
        li.append(input, label);
        group.optionsEl.append(li);
      });

      this.layoutOptions(group);
    });
  }

  /**
   * Decide how many columns the open list needs. The values are handed to CSS
   * as custom properties; the grid in snippets/blog-filter-bar.liquid fills the
   * first column all the way down before it starts the second, so the list is
   * read the way a list is read.
   *
   * Counted on what is actually showing, not on everything, so a search that
   * narrows twenty values down to three collapses the second column with it.
   */
  layoutOptions(group) {
    const visible = Array.from(
      group.optionsEl.querySelectorAll('[data-blog-filter-option]')
    ).filter((li) => li.style.display !== 'none').length;

    // "+ 1": the split happens *at* eight, not above it, so eight colours read
    // as four and four. The rows are then divided evenly between the columns —
    // eight and one would look like a mistake next to six and six.
    const columns = Math.min(
      MAX_OPTION_COLUMNS,
      Math.max(1, Math.ceil((visible + 1) / OPTIONS_PER_COLUMN))
    );
    const rows = Math.max(1, Math.ceil(visible / columns));

    group.optionsEl.style.setProperty('--blog-filter-option-columns', String(columns));
    group.optionsEl.style.setProperty('--blog-filter-option-rows', String(rows));
  }

  bindEvents() {
    this.groups.forEach((group) => {
      if (group.el.hidden) return;

      if (group.chip) {
        group.toggle.addEventListener('click', () => {
          // Off unless every value it stands for is on, so a half-on state
          // (from a link, or from the dropdown of the same filter) completes
          // rather than clears.
          const isOn = group.chip.every((value) => group.bucket.selected.has(value));
          group.chip.forEach((value) => {
            if (isOn) group.bucket.selected.delete(value);
            else group.bucket.selected.add(value);
          });
          this.syncInputs();
          this.requestApply();
          this.writeUrl();
        });
        return;
      }

      group.toggle.addEventListener('click', () => this.toggleGroup(group));

      group.optionsEl.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.checked) group.bucket.selected.add(input.value);
        else group.bucket.selected.delete(input.value);
        this.syncInputs();
        this.requestApply();
        this.writeUrl();
      });

      if (group.searchEl) {
        group.searchEl.addEventListener('input', () => {
          const query = normalise(group.searchEl.value);
          let visible = 0;
          group.optionsEl.querySelectorAll('[data-blog-filter-option]').forEach((li) => {
            const match = !query || li.getAttribute('data-blog-filter-option').includes(query);
            // display, not the hidden attribute: the row carries "flex", which
            // would win over [hidden] { display: none }.
            li.style.display = match ? '' : 'none';
            if (match) visible += 1;
          });
          if (group.noOptionsEl) {
            group.noOptionsEl.style.display = visible > 0 ? 'none' : '';
            group.noOptionsEl.textContent = this.strings.noMatches.replace(
              '[terms]',
              group.searchEl.value
            );
          }
          this.layoutOptions(group);
        });
      }
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearAll());
    }

    document.addEventListener('click', (event) => {
      if (!this.bar.contains(event.target)) this.closeAll();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeAll();
    });

    window.addEventListener('popstate', () => {
      this.readUrl();
      this.syncInputs();
      this.apply();
    });
  }

  toggleGroup(group) {
    const isOpen = !group.panel.classList.contains('hidden');
    this.closeAll();
    if (isOpen) return;

    group.panel.classList.remove('hidden');
    group.toggle.setAttribute('aria-expanded', 'true');
    this.setIcon(group, true);
    if (group.searchEl && group.searchIsShown) group.searchEl.focus();
  }

  closeAll() {
    this.groups.forEach((group) => {
      if (group.el.hidden || group.chip) return;
      group.panel.classList.add('hidden');
      group.toggle.setAttribute('aria-expanded', 'false');
      this.setIcon(group, false);
    });
  }

  setIcon(group, open) {
    const chevron = group.toggle.querySelector('[data-blog-filter-chevron]');
    if (chevron) chevron.classList.toggle('rotate-180', open);
    const plus = group.toggle.querySelector('[data-blog-filter-plus]');
    const minus = group.toggle.querySelector('[data-blog-filter-minus]');
    if (plus) plus.classList.toggle('hidden', open);
    if (minus) minus.classList.toggle('hidden', !open);
  }

  /**
   * Same as apply(), but the listing dips out and back in first, so a filter
   * reads as the projects changing rather than as the page jumping. Used for
   * everything the reader does; the first render on page load calls apply()
   * directly, since there is nothing to fade from.
   */
  requestApply() {
    const stillFrames = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (stillFrames) {
      this.apply();
      return;
    }

    this.results.setAttribute('data-blog-filter-busy', '');
    clearTimeout(this.fadeTimer);
    this.fadeTimer = setTimeout(() => {
      this.apply();
      this.results.removeAttribute('data-blog-filter-busy');
    }, FADE_MS);
  }

  /** Show the cards that match every active filter. */
  apply() {
    // Per filter, not per button: two buttons standing for the same filter are
    // one condition to meet, and meeting either of them is enough.
    const active = Array.from(this.buckets.values()).filter((bucket) => bucket.selected.size > 0);
    let matches = 0;

    this.cards.forEach((card) => {
      const isMatch = active.every((bucket) => {
        const values = cardValues(card, bucket.key).map(normalise);
        return values.some((value) => bucket.selected.has(value));
      });
      card.hidden = !isMatch;
      if (isMatch) matches += 1;
    });

    this.groups.forEach((group) => {
      if (group.el.hidden) return;

      if (group.chip) {
        const isOn = group.chip.every((value) => group.bucket.selected.has(value));
        group.toggle.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        group.el.toggleAttribute('data-blog-filter-active', isOn);
        return;
      }

      // Counted on this dropdown's own values, so a choice made through one of
      // the single words does not show up as a number on a dropdown that never
      // offered it.
      const count = group.options.filter((option) => group.bucket.selected.has(option.id)).length;
      // Non-breaking space: a plain one collapses away against the label.
      group.countEl.textContent = count > 0 ? ` (${count})` : '';
      // Marks the whole dropdown as holding a choice. The stylesheet turns that
      // into a line under the word, which is easier to spot in a row of seven
      // filters than the count on its own.
      group.el.toggleAttribute('data-blog-filter-active', count > 0);
    });

    if (this.countEl) {
      this.countEl.textContent =
        active.length > 0
          ? this.strings.resultCount
              .replace('[count]', String(matches))
              .replace('[total]', String(this.cards.length))
          : '';
    }
    if (this.clearButton) this.clearButton.classList.toggle('hidden', active.length === 0);
    if (this.emptyState) this.emptyState.hidden = matches > 0;

    this.updatePlaceholder(matches);
  }

  /**
   * The grid ends with a spacer that fills the last row. It is sized on the
   * server for the unfiltered count, so it has to be recalculated whenever the
   * number of visible cards changes.
   */
  updatePlaceholder(matches) {
    if (!this.placeholder) return;
    const onLastRow = matches % this.perRow;
    if (matches === 0 || onLastRow === 0) {
      // The spacer is lg:block, so it has to be hidden through display.
      this.placeholder.style.display = 'none';
      return;
    }
    this.placeholder.style.display = '';
    this.placeholder.style.setProperty(
      '--placeholder-width',
      String((this.perRow - onLastRow) * this.itemColumnWidth)
    );
  }

  clearAll() {
    this.buckets.forEach((bucket) => bucket.selected.clear());
    this.syncInputs();
    this.requestApply();
    this.writeUrl();
  }

  /** Push the chosen values back onto the inputs (URL load, clear all). */
  syncInputs() {
    this.groups.forEach((group) => {
      if (group.el.hidden || group.chip) return;
      group.optionsEl.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = group.bucket.selected.has(input.value);
      });
    });
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    this.buckets.forEach((bucket) => {
      bucket.selected.clear();
      // One parameter per value rather than one comma separated parameter:
      // a value can itself contain a comma ("Paris, France"), which would
      // otherwise be read back as two filters that match nothing.
      params
        .getAll(PARAM_PREFIX + bucket.key)
        .map((value) => normalise(value))
        .filter(Boolean)
        // Ignore values that no longer exist, so an old link never filters
        // everything away.
        .filter((value) => bucket.options.some((option) => option.id === value))
        .forEach((value) => bucket.selected.add(value));
    });
    this.syncInputs();
  }

  writeUrl() {
    const params = new URLSearchParams(window.location.search);
    this.buckets.forEach((bucket) => {
      const name = PARAM_PREFIX + bucket.key;
      // Deleted either way: what is left is written back from scratch, so a
      // value that has just been switched off cannot linger in the address.
      params.delete(name);
      if (bucket.selected.size === 0) return;
      // Store the readable spelling, not the lowercased comparison key.
      bucket.options
        .filter((option) => bucket.selected.has(option.id))
        .forEach((option) => params.append(name, option.label));
    });

    const query = params.toString();
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    window.history.replaceState({}, '', url);
  }
}

document.querySelectorAll('[data-blog-filter-root]').forEach((root) => new BlogFilter(root));
